import React, { Component, createContext } from 'react';
import { Api } from '../lib/api';

interface DeviceType {
    id: number;
    name: string;
}

interface Station {
    id: number;
    name: string;
    createdAt?: string;
    updatedAt?: string;
    devices: number[];
    peripherals: number[];
}

interface Device {
    id: number;
    uid: string;
    stationId: number | null;
    assigned?: boolean;
    [key: string]: any;
}

interface Peripheral {
    id: number;
    name: string;
    deviceTypeId: number;
}

export interface HardwareState {
    deviceTypes: {
        byId: Record<number, DeviceType>;
    };
    devices: {
        byId: Record<number, Device>;
        allIds: number[];
        unassigned: number[];
    };
    stations: {
        byId: Record<number, Station>;
        allIds: number[];
        newStationIndex: number;
    };
    peripherals: {
        byId: Record<number, Peripheral>;
    };
    pollInProgress: boolean;
}

export interface HardwareContextValue extends HardwareState {
    fetchStations: () => Promise<void>;
    fetchDevices: () => Promise<{ devices: Device[] } | undefined>;
    fetchPeripherals: () => Promise<void>;
    getDeviceTypes: () => Promise<void>;
    assignDeviceToStation: (deviceId: number | null, stationId: number | null) => Promise<void>;
    createStation: (stationName: string) => Promise<void>;
    deleteStation: (stationId: number) => Promise<void>;
    renameStation: (stationId: number, name: string) => Promise<void>;
}

export const HardwareContext = createContext<HardwareContextValue | null>(null);

export const initialState: HardwareState = {
    deviceTypes: {
        byId: {}
    },
    devices: {
        byId: {},
        allIds: [],
        unassigned: []
    },
    stations: {
        byId: {},
        allIds: [],
        newStationIndex: 0
    },
    peripherals: {
        byId: {}
    },
    pollInProgress: false
};

// --- Pure state transformation functions ---
// These are pure (prevState, data) => newState functions with no side effects.
// They can be tested directly without rendering any React components.

/**
 * Reconciles the station list from the API with the current state.
 * Adds new stations, updates existing ones, and removes any that
 * are no longer present in the API response.
 */
export function processStationsResponse(prevState: HardwareState, stations: any[]): HardwareState {
    // Shallow-copy the stations slice so we don't mutate prevState
    const newState = {
        ...prevState,
        stations: {
            ...prevState.stations,
            byId: { ...prevState.stations.byId },
            allIds: [...prevState.stations.allIds]
        }
    };

    // Track which existing stations we haven't seen in the API response yet.
    // Any left over after processing are stale and will be removed.
    let unseenStations = [...prevState.stations.allIds];

    stations.forEach((station) => {
        let index = unseenStations.indexOf(station.id);

        // New station — not in current state
        if (index === -1) {
            newState.stations.byId[station.id] = {
                id: station.id,
                name: station.name,
                createdAt: station.createdAt,
                updatedAt: station.updatedAt,
                devices: station.devices.map((device: any) => device.id),
                peripherals: station.peripherals.map((peripheral: any) => peripheral.id)
            };

            newState.stations.allIds.push(station.id);
            return;
        }

        // Existing station — mark as seen and update its properties
        unseenStations.splice(index, 1);

        newState.stations.byId[station.id] = {
            ...prevState.stations.byId[station.id],
            name: station.name,
            createdAt: station.createdAt,
            updatedAt: station.updatedAt,
            devices: station.devices.map((a: any) => a.id),
            peripherals: station.peripherals.map((a: any) => a.id)
        };
    });

    // Remove stations that were not in the API response (stale)
    unseenStations.forEach((station) => {
        delete newState.stations.byId[station];
        newState.stations.allIds.splice(newState.stations.allIds.indexOf(station), 1);
    });

    return newState;
}

/**
 * Reconciles the device list from the API with the current state.
 * Adds new devices, updates existing ones, removes stale ones,
 * and maintains the unassigned list based on stationId.
 */
export function processDevicesResponse(prevState: HardwareState, devices: any[]): HardwareState {
    // Shallow-copy the devices slice so we don't mutate prevState
    const newState = {
        ...prevState,
        devices: {
            ...prevState.devices,
            byId: { ...prevState.devices.byId },
            allIds: [...prevState.devices.allIds],
            unassigned: [...prevState.devices.unassigned]
        }
    };

    // Track which existing devices we haven't seen in the API response yet.
    // Any left over after processing are stale and will be removed.
    let unseenDevices = [...prevState.devices.allIds];

    devices.forEach((device) => {
        let index = unseenDevices.indexOf(device.id);

        // New device — not in current state
        if (index === -1) {
            newState.devices.byId[device.id] = device;

            if (device.stationId === null) {
                newState.devices.unassigned.push(device.id);
            }

            newState.devices.allIds.push(device.id);
            return;
        }

        // Existing device — mark as seen and merge updated properties
        unseenDevices.splice(index, 1);

        newState.devices.byId[device.id] = {
            ...newState.devices.byId[device.id],
            ...device
        };

        // Sync unassigned list: add if newly unassigned, remove if newly assigned
        let unassignedIndex = newState.devices.unassigned.indexOf(device.id);
        if (unassignedIndex === -1 && device.stationId === null) {
            newState.devices.unassigned.push(device.id);
        } else if (unassignedIndex !== -1 && device.stationId !== null) {
            newState.devices.unassigned.splice(unassignedIndex, 1);
        }
    });

    // Remove devices that were not in the API response (stale)
    unseenDevices.forEach((unseenDevice) => {
        delete newState.devices.byId[unseenDevice];
        newState.devices.allIds.splice(newState.devices.allIds.indexOf(unseenDevice), 1);
        let index = newState.devices.unassigned.indexOf(unseenDevice);

        if (index !== -1) {
            newState.devices.unassigned.splice(index, 1);
        }
    });

    return newState;
}

/**
 * Replaces the entire peripherals byId map with the API response.
 */
export function processPeripheralsResponse(peripherals: any[]): Pick<HardwareState, 'peripherals'> {
    // Full replacement — index all peripherals by id
    const peripheralsById: Record<number, Peripheral> = {};

    peripherals.forEach((peripheral) => {
        peripheralsById[peripheral.id] = peripheral;
    });

    return { peripherals: { byId: peripheralsById } };
}

/**
 * Replaces the entire deviceTypes byId map with the API response.
 */
export function processDeviceTypesResponse(deviceTypes: any[]): Pick<HardwareState, 'deviceTypes'> {
    // Full replacement — index all device types by id, keeping only id and name
    const deviceTypesById: Record<number, DeviceType> = {};

    deviceTypes.forEach((deviceType) => {
        deviceTypesById[deviceType.id] = {
            id: deviceType.id,
            name: deviceType.name
        };
    });

    return { deviceTypes: { byId: deviceTypesById } };
}

/**
 * Moves a device between stations (or to/from unassigned).
 * Updates the device's stationId and both the source and target station's device lists.
 */
export function processAssignDeviceSuccess(prevState: HardwareState, deviceId: number, stationId: number | null): HardwareState {
    // Update the device's stationId and shallow-copy both slices
    const newState = {
        ...prevState,
        devices: {
            ...prevState.devices,
            byId: {
                ...prevState.devices.byId,
                [deviceId]: {
                    ...prevState.devices.byId[deviceId],
                    stationId: stationId
                }
            },
            unassigned: [...prevState.devices.unassigned]
        },
        stations: {
            ...prevState.stations,
            byId: { ...prevState.stations.byId }
        }
    };

    // Remove device from its previous location (unassigned list or old station)
    let oldStationId = prevState.devices.byId[deviceId].stationId;

    if (oldStationId === null) {
        // Was unassigned — remove from unassigned list
        let index = newState.devices.unassigned.indexOf(deviceId);
        if (index !== -1) {
            newState.devices.unassigned.splice(index, 1);
        }
    } else {
        // Was in a station — remove from that station's device list
        newState.stations.byId[oldStationId] = {
            ...prevState.stations.byId[oldStationId],
            devices: [...prevState.stations.byId[oldStationId].devices]
        };

        let index = newState.stations.byId[oldStationId].devices.indexOf(deviceId);
        if (index !== -1) {
            newState.stations.byId[oldStationId].devices.splice(index, 1);
        }
    }

    // Add device to its new location (unassigned list or target station)
    if (stationId === null) {
        newState.devices.unassigned.push(deviceId);
    } else {
        newState.stations.byId[stationId] = {
            ...prevState.stations.byId[stationId],
            devices: [...prevState.stations.byId[stationId].devices, deviceId]
        };
    }

    return newState;
}

/**
 * Adds a newly created station to the state with empty device and peripheral lists.
 */
export function processCreateStationSuccess(prevState: HardwareState, stationId: number, name: string): HardwareState {
    return {
        ...prevState,
        stations: {
            ...prevState.stations,
            byId: {
                ...prevState.stations.byId,
                [stationId]: {
                    id: stationId,
                    name: name,
                    devices: [],
                    peripherals: []
                }
            },
            allIds: [...prevState.stations.allIds, stationId]
        }
    };
}

/**
 * Removes a station and moves all of its devices to the unassigned list.
 */
export function processDeleteStationSuccess(prevState: HardwareState, stationId: number): HardwareState {
    const newState = {
        ...prevState,
        devices: {
            ...prevState.devices,
            byId: { ...prevState.devices.byId },
            unassigned: [...prevState.devices.unassigned]
        },
        stations: {
            ...prevState.stations,
            byId: { ...prevState.stations.byId },
            allIds: [...prevState.stations.allIds]
        }
    };

    // Unassign all devices that belonged to this station
    prevState.stations.byId[stationId].devices.forEach((v) => {
        newState.devices.byId[v] = {
            ...prevState.devices.byId[v],
            stationId: null
        };

        newState.devices.unassigned.push(v);
    });

    // Remove the station itself
    let index = newState.stations.allIds.indexOf(stationId);
    if (index !== -1) {
        newState.stations.allIds.splice(index, 1);
    }

    delete newState.stations.byId[stationId];

    return newState;
}

/**
 * Updates a station's name, preserving all other station properties.
 */
export function processRenameStationSuccess(prevState: HardwareState, stationId: number, name: string): HardwareState {
    return {
        ...prevState,
        stations: {
            ...prevState.stations,
            byId: {
                ...prevState.stations.byId,
                [stationId]: {
                    ...prevState.stations.byId[stationId],
                    name: name
                }
            }
        }
    };
}

// --- Provider component ---

export class HardwareProvider extends Component<{ children?: React.ReactNode }, HardwareState> {
    state = initialState;

    fetchStations = async () => {
        try {
            const stations = await Api.getStations();
            this.setState((prevState) => processStationsResponse(prevState, stations));
        } catch (e) {
            console.warn('Failed to fetch stations:', e);
        }
    };

    fetchDevices = async (): Promise<{ devices: Device[] } | undefined> => {
        if (this.state.pollInProgress) {
            return;
        }

        this.setState({ pollInProgress: true });

        try {
            const devices = await Api.getDevices();
            this.setState((prevState) => processDevicesResponse(prevState, devices));
            return { devices };
        } catch (e) {
            console.warn('Failed to fetch devices:', e);
        } finally {
            this.setState({ pollInProgress: false });
        }
    };

    fetchPeripherals = async () => {
        try {
            const peripherals = await Api.getPeripherals();
            this.setState(processPeripheralsResponse(peripherals));
        } catch (e) {
            console.warn('Failed to fetch peripherals:', e);
        }
    };

    getDeviceTypes = async () => {
        try {
            const deviceTypes = await Api.getDeviceTypes();
            this.setState(processDeviceTypesResponse(deviceTypes));
        } catch (e) {
            console.warn('Failed to fetch device types:', e);
        }
    };

    assignDeviceToStation = async (deviceId: string | number | null, stationId: string | number | null) => {
        if (deviceId !== null) {
            deviceId = parseInt(deviceId as any, 10);
        }

        if (stationId !== null) {
            stationId = parseInt(stationId as any, 10);
        }

        const { devices } = this.state;

        if (deviceId === null || !(deviceId in devices.byId)) {
            return;
        }

        if (devices.byId[deviceId].assigned) {
            return;
        }

        if (stationId === devices.byId[deviceId].stationId) {
            return;
        }

        try {
            await Api.updateDevice(deviceId, { stationId });
            this.setState((prevState) => processAssignDeviceSuccess(prevState, deviceId as number, stationId as number | null));
        } catch (e) {
            console.warn('Failed to assign device:', e);
        }
    };

    createStation = async (stationName: string) => {
        try {
            const station = await Api.createStation(stationName);
            this.setState((prevState) => processCreateStationSuccess(prevState, station.id, stationName));
        } catch (e) {
            console.warn('Failed to create station:', e);
        }
    };

    deleteStation = async (stationId: number) => {
        try {
            await Api.deleteStation(stationId);
            this.setState((prevState) => processDeleteStationSuccess(prevState, stationId));
        } catch (e) {
            console.warn('Failed to delete station:', e);
        }
    };

    renameStation = async (stationId: number, name: string) => {
        try {
            await Api.updateStation(stationId, { name });
            this.setState((prevState) => processRenameStationSuccess(prevState, stationId, name));
        } catch (e) {
            console.warn('Failed to rename station:', e);
        }
    };

    render() {
        const value: HardwareContextValue = {
            ...this.state,
            fetchStations: this.fetchStations,
            fetchDevices: this.fetchDevices,
            fetchPeripherals: this.fetchPeripherals,
            getDeviceTypes: this.getDeviceTypes,
            assignDeviceToStation: this.assignDeviceToStation,
            createStation: this.createStation,
            deleteStation: this.deleteStation,
            renameStation: this.renameStation
        };

        return <HardwareContext.Provider value={value}>{this.props.children}</HardwareContext.Provider>;
    }
}
